import React from 'react';

export default function ApiErrorModal({
  isOpen,
  title = 'Service Unavailable',
  message = 'An error occurred while communicating with the server.',
  details,
  onTryAgain,
  onReload,
  onCancel,
  isRetrying = false,
}) {
  if (!isOpen) return null;

  const handleReload = () => {
    if (typeof onReload === 'function') {
      onReload();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm transition-opacity animate-fadeIn">
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="error-modal-title"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <h3 id="error-modal-title" className="text-base font-bold text-slate-900 dark:text-white">
                {title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                {message}
              </p>
              {details && (
                <div className="mt-3 p-2.5 rounded-lg bg-slate-100 dark:bg-slate-900/60 text-xs font-mono text-slate-600 dark:text-slate-400 break-words border border-slate-200 dark:border-slate-700/60">
                  {details}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/40 border-t border-slate-100 dark:border-slate-700 flex items-center justify-end gap-2.5">
          {onCancel && (
            <button
              type="button"
              disabled={isRetrying}
              onClick={onCancel}
              className="px-3.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          )}

          <button
            type="button"
            disabled={isRetrying}
            onClick={handleReload}
            className="px-3.5 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-xl transition-colors disabled:opacity-50"
          >
            Reload Page
          </button>

          {onTryAgain && (
            <button
              type="button"
              disabled={isRetrying}
              onClick={onTryAgain}
              className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl shadow-md shadow-indigo-600/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              {isRetrying ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  <span>Retrying...</span>
                </>
              ) : (
                <span>Try Again</span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
